// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Field-name alignment for the ADR-004 D1 candidate-collection response
 * (currently the single evidenced O1 producer, AU0008 / Update Firmware Over
 * Serial's `AccountabilityAPIv1.GetLatestAccountability`).
 *
 * ProcessServiceRequest.ds relays that AU's raw response straight onto
 * response_payload with no renaming: `{ success, data: { record_id,
 * device_anchor, upload_status, firmware_package_ref, correlation_id,
 * write_started, fault_origin, added_time, created_by } }`. The widget's
 * contract (../contracts/requests.ts CandidateOperation / CollectCandidatesResponse)
 * uses different names (referenceId, executionTime, executedBy) and needs a
 * `candidates` / `queriedUpdaterAUs` shape the raw record does not carry.
 *
 * Each Updater AU is handled one at a time (ADR-004 D1 O1 open set): `success:
 * true` with a data payload → the AU responded and contributes one candidate;
 * anything else (no response, success:false, missing data) → the AU is
 * recorded as consulted but not responded, and collection moves on. If no
 * Updater AU responds, `candidates` is empty — a valid ADR-004 D1 outcome
 * ("resolved_no_operation"), not an error.
 */

import { toUnknownableBoolean, toUnknownableString } from "@/contracts/display";
import type {
  CandidateOperation,
  CollectCandidatesResponse,
} from "@/contracts/requests";

function toStringOrEmpty(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function toStringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function noResponse(deploymentChannel: string): CollectCandidatesResponse {
  return {
    candidates: [],
    queriedUpdaterAUs: [{ deploymentChannel, responded: false }],
  };
}

/**
 * Map a raw updater-AU accountability response_payload onto
 * CollectCandidatesResponse. Idempotent: a payload that already carries a
 * `candidates` array (e.g. the mock transport's contract-shaped fixtures)
 * passes through unchanged.
 *
 * @param deploymentChannel — Full name of the producing/updater AU (ADR-004 D4).
 */
export function mapUpdaterAuResponse(
  raw: unknown,
  deploymentChannel: string,
): CollectCandidatesResponse {
  if (typeof raw !== "object" || raw === null) {
    return noResponse(deploymentChannel);
  }
  const rec = raw as Record<string, unknown>;

  // Already-mapped pass-through (e.g. mock transport fixtures).
  if (Array.isArray(rec["candidates"])) {
    return rec as unknown as CollectCandidatesResponse;
  }

  // data: null → the AU responded successfully but has no operation record
  // for this device. Treat as responded with no candidate (ADR-004 D1).
  if (rec["data"] === null) {
    return {
      candidates: [],
      queriedUpdaterAUs: [{ deploymentChannel, responded: true }],
    };
  }

  // Data arrives here only on success — map directly.
  const executionTime = toStringOrUndefined(rec["added_time"]);
  const candidate: CandidateOperation = {
    deploymentChannel,
    referenceId: toStringOrEmpty(rec["firmware_package_ref"]),
    execution: {
      executionStatus: toUnknownableString(rec["upload_status"]),
      executionTime: toUnknownableString(rec["added_time"]),
      executedBy: toUnknownableString(rec["created_by"]),
      writeStarted: toUnknownableBoolean(rec["write_started"]),
      faultOrigin: toUnknownableString(rec["fault_origin"]),
    },
    ...(executionTime !== undefined ? { executionTime } : {}),
  };

  return {
    candidates: [candidate],
    queriedUpdaterAUs: [{ deploymentChannel, responded: true }],
  };
}
